import type { BrowserFeatures } from "../types";
import type { Collector, CollectorContext } from "../collectors/types";
import { isBrowser } from "../collectors/types";
import { browserCollector } from "../collectors/navigator";
import { canvasCollector } from "../collectors/canvas";
import { audioCollector } from "../collectors/audio";
import { fontsCollector } from "../collectors/fonts";
import { webglCollector } from "../collectors/webgl";
import { displayCollector, stableDisplayCollector } from "../collectors/screen";
import { storageCollector } from "../collectors/storage";
import { botCollector } from "../collectors/bot";
import { networkCollector } from "../collectors/network";
import { getVisitInfo, getIncognitoProbeContext } from "../collectors/visit";

const DEFAULT_TIMEOUT_MS = 2000;

/**
 * Run a collector with a per-collector timeout. Never throws — failures and
 * timeouts both resolve to undefined so one bad signal can't fail the whole
 * fingerprint call. Exported for unit testing.
 */
export async function runCollector<T>(
  collector: Collector<T>,
  ctx: CollectorContext,
): Promise<T | undefined> {
  const timeoutMs = collector.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  return new Promise<T | undefined>((resolve) => {
    let settled = false;
    const settle = (v: T | undefined) => {
      if (settled) return;
      settled = true;
      resolve(v);
    };

    const timer = setTimeout(() => settle(undefined), timeoutMs);

    Promise.resolve()
      .then(() => collector.collect(ctx))
      .then((value) => {
        clearTimeout(timer);
        settle(value);
      })
      .catch(() => {
        clearTimeout(timer);
        settle(undefined);
      });
  });
}

/**
 * Collect all browser fingerprint features. Two-phase:
 *   Phase 1: browser detection (cheap, downstream collectors branch on it).
 *   Phase 2: all other collectors run via Promise.allSettled with timeouts.
 *
 * Per-collector timeouts mean a hung audio decode or storage probe can't
 * stall the whole call. Failed collectors silently degrade to undefined —
 * matches the previous monolithic collector.ts behavior.
 */
export async function collectFingerprintFeatures(): Promise<BrowserFeatures> {
  const ctx: CollectorContext = { isBrowser };

  // Phase 1: browser detection. Many downstream collectors branch on this.
  const browser = (await runCollector(browserCollector, ctx)) ?? {
    isBrave: false,
    isSafari: false,
    isFirefox: false,
    isIosChrome: false,
    ua: "",
    brands: [] as string[],
    family: "unknown",
    version: "",
    engine: "unknown",
  };
  ctx.browser = browser;

  // Phase 2: parallel collectors + uaCh high-entropy values.
  const nav: any = isBrowser ? navigator : {};
  const uaChPromise: Promise<any> =
    isBrowser && nav.userAgentData?.getHighEntropyValues
      ? Promise.resolve(
          nav.userAgentData.getHighEntropyValues([
            "architecture",
            "model",
            "bitness",
            "platform",
            "platformVersion",
            "fullVersionList",
          ]),
        ).catch(() => undefined)
      : Promise.resolve(undefined);

  const [
    canvasResult,
    audioResult,
    fontsResult,
    glResult,
    displayResult,
    stableDisplayResult,
    storageResult,
    botResult,
    networkResult,
    uaChData,
  ] = await Promise.all([
    runCollector(canvasCollector, ctx),
    runCollector(audioCollector, ctx),
    runCollector(fontsCollector, ctx),
    runCollector(webglCollector, ctx),
    runCollector(displayCollector, ctx),
    runCollector(stableDisplayCollector, ctx),
    runCollector(storageCollector, ctx),
    runCollector(botCollector, ctx),
    runCollector(networkCollector, ctx),
    uaChPromise,
  ]);

  // Stable display fallback if its collector timed out — salt depends on it.
  const stableDisplay = stableDisplayResult ?? {
    primaryW: null,
    primaryH: null,
    dpr: null,
    depth: null,
    source: "ssr" as const,
  };

  const uaCh =
    isBrowser && nav.userAgentData
      ? {
          brands: nav.userAgentData.brands,
          platform: nav.userAgentData.platform,
          platformVersion: nav.userAgentData.platformVersion,
          model: nav.userAgentData.model,
          mobile: nav.userAgentData.mobile,
          fullVersionList: uaChData,
        }
      : undefined;

  const features: BrowserFeatures = {
    browser,
    uaCh,
    browserFamily: (nav.userAgent || browser.family).slice(0, 200) || "unknown",
    screen: stableDisplay,
    screenRaw: isBrowser
      ? {
          w: screen.width,
          h: screen.height,
          dpr: window.devicePixelRatio || 1,
          colorDepth: screen.colorDepth,
        }
      : undefined,
    display: displayResult ?? { gamut: "srgb", hdr: false },
    tz: isBrowser ? Intl.DateTimeFormat().resolvedOptions().timeZone : undefined,
    locales: isBrowser ? navigator.languages || [navigator.language] : [],
    hw: isBrowser
      ? {
          mem: (nav as any).deviceMemory,
          cores: navigator.hardwareConcurrency,
          touch: matchMedia("(pointer:coarse)").matches,
        }
      : undefined,
    gl: glResult ?? {},
    canvas: canvasResult ?? {},
    audio: audioResult ?? {},
    fonts: fontsResult ?? [],
    storage: storageResult ?? {},
    network: networkResult ?? {},
    visit: getVisitInfo(),
    incognitoContext: getIncognitoProbeContext(browser),
    bot: botResult ?? {
      webdriver: false,
      outerSizeZero: false,
      automationProps: [],
      pluginCount: 0,
      chromeRuntime: null,
      languagesEmpty: false,
      screenZero: false,
      hasFocus: null,
    },
  };

  return features;
}
